import { TResApi, TResDataListApi } from '@src/configs/interface.config'
import { checkAuth } from '@src/libs/localStorage'
import { TQueryReward, TReward } from '@src/modules/reward'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'
import { notification } from 'antd'
import { NSuccess } from '@src/configs/const.config'
import {
  getLisReward,
  getLisRewardApp,
  getRewardById,
  getRewardTotal,
  patchRewardById,
  postReward,
  removeRewardById,
  removeRewardByIds,
} from '@queries/apis'
import { DETAIL_REWARD, LIST_REWARD, LIST_REWARD_INFINITE, TOTAL_REWARD } from '@queries/keys'

import { queryClient } from '..'

export const useQueryListReward = (params: TQueryReward, token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResDataListApi<TReward[]>>(
    [LIST_REWARD, JSON.stringify(params)],
    () => getLisReward(params, accessToken),
    {
      enabled: !!accessToken,
    },
  )
}

export const useMutationRemoveRewardById = () =>
  useMutation(removeRewardById, {
    onSuccess: (res: TResApi, id) => {
      queryClient.refetchQueries([LIST_REWARD]).then()
      queryClient.removeQueries([DETAIL_REWARD, id])
      notification.success({ message: NSuccess, description: res?.message })
    },
  })
export const useQueryRewardById = (id: string, token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<TReward>>([DETAIL_REWARD, id], () => getRewardById(id, accessToken), {
    enabled: !!accessToken && !!id,
  })
}
export const useMutationPostReward = () =>
  useMutation(postReward, {
    onSuccess: (res: TResApi<TReward>) => {
      queryClient.refetchQueries([LIST_REWARD]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
  })
export const useMutationPatchRewardById = () =>
  useMutation(({ id, data }: { id: string; data: TReward }) => patchRewardById(id, data), {
    onSuccess: (res: TResApi, { id }) => {
      queryClient.refetchQueries([LIST_REWARD]).then()
      queryClient.invalidateQueries([DETAIL_REWARD, id]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
  })
export const useMutationRemoveRewardByIds = () =>
  useMutation(removeRewardByIds, {
    onSuccess: (res: TResApi) => {
      queryClient.refetchQueries([LIST_REWARD]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
  })
export const useQueryRewardTotal = (token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<number>>([TOTAL_REWARD], () => getRewardTotal(token as string), { enabled: !!accessToken })
}

export const useInfiniteQueryListReward = (params: TQueryReward, token?: string) => {
  const accessToken = token || checkAuth()

  return useInfiniteQuery<TResDataListApi<TReward[]>>({
    queryFn: ({ pageParam = 1 }) => getLisRewardApp({ ...params, page: pageParam }, accessToken),
    queryKey: [LIST_REWARD_INFINITE, params],
    getNextPageParam: (lastPage) =>
      lastPage?.page < Math.ceil(lastPage.total / lastPage.limit) ? lastPage.page + 1 : undefined,
    refetchOnWindowFocus: true,
    retry: 2,
    enabled: !!accessToken,
  })
}
